import type { components } from '../types/api/generated';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';

export type CaptaincyPollResults = components['schemas']['CaptaincyPoll'];

/** The error code ffh-backend answers a vote with once the deadline has passed. */
export const POLL_CLOSED_ERROR_CODE = 'POLL_CLOSED';

/** Stands in for the backend's code when a vote fails without one (a network error). */
const VOTE_FAILED_ERROR_CODE = 'VOTE_FAILED';

/**
 * The votes so far in a gameweek's poll. With a token the response also
 * carries the member's own pick in `myVote`.
 */
export const fetchCaptaincyPollResults = async (
  gameweek: number,
  accessToken: string | null
): Promise<CaptaincyPollResults> => {
  const { data, error } = await ffhClient().GET(
    '/captaincy-poll/gameweeks/{gameweek}',
    {
      params: { path: { gameweek } },
      headers: accessToken ? authHeader(accessToken) : undefined
    }
  );
  if (error || !data) throw new Error('Failed to load the captaincy poll');
  return data.data;
};

/**
 * Records or changes the member's pick, and answers with the poll as it now
 * stands. Throws an error whose message is the backend's error code, so the
 * caller can tell a closed poll from a failure.
 */
export const voteInCaptaincyPoll = async (
  accessToken: string,
  gameweek: number,
  playerOptaId: string
): Promise<CaptaincyPollResults> => {
  const { data, error } = await ffhClient().PUT(
    '/captaincy-poll/gameweeks/{gameweek}/vote',
    {
      headers: authHeader(accessToken),
      params: { path: { gameweek } },
      body: { playerOptaId }
    }
  );
  if (error || !data) {
    throw new Error(error?.error?.code ?? VOTE_FAILED_ERROR_CODE);
  }
  return data.data;
};
